import { useCallback, useMemo } from 'react';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import {
  ensureBooleanSampleValues,
  type LoadFieldValues,
  type ParsedToken,
} from '@gonfalon/dataviz-experimental/QueryBuilder';
import type { FieldOptions } from '@gonfalon/dataviz-experimental/types';
import { useLatestRef } from '@gonfalon/react/useLatestRef';
import { getContextAttributeValuesQuery } from '@gonfalon/rest-api/queries/contexts';
import { useQueryClient } from '@tanstack/react-query';

import { CONTEXT_ATTRS_PREFIX, CONTEXT_KIND_FIELD } from './metricEventFilterFields';
import { committedContextAttributeFields, scopeFieldOptions } from './seedAndScopeFieldOptions';
import {
  PINNED_KIND_ATTRIBUTE_LIMIT,
  useContextAttributeNameOptions,
  useContextScopedFieldOptions,
} from './useMetricEventSuggestions';

const PINNED_KIND_BREADTH_RESERVED = 5;
const PINNED_KIND_ATTRIBUTE_VALUE_LIMIT = 50;

export function useContextBarFieldOptions(
  projectKey: string,
  fieldOptions: FieldOptions[],
  selectedKinds: string[],
  parsedQuery: ParsedToken[],
): {
  scopedFieldOptions: FieldOptions[];
  onLoadValues: LoadFieldValues;
  isAttributeCatalogPending: boolean;
  showEmptyPinnedAttributesMessage: boolean;
} {
  const environmentKey = useSelectedEnvironmentKey({ optional: true });
  const queryClient = useQueryClient();
  const serverScoped = useContextScopedFieldOptions(projectKey, selectedKinds);
  const breadth = useContextAttributeNameOptions(projectKey, selectedKinds);
  const serverScopedOptions = serverScoped.options;
  const breadthOptions = breadth.options;
  const isAttributeCatalogPending = serverScoped.isPending || breadth.isPending;
  const valueParamsByFieldRef = useLatestRef(breadth.valueParamsByField);

  const scopedFieldOptions = useMemo(() => {
    const scopedSuggestions = serverScopedOptions;
    const breadthAttrs = breadthOptions;
    const base = scopeFieldOptions(fieldOptions, selectedKinds);
    const suggestionOptions: FieldOptions[] = [];
    const seen = new Set<string>();
    for (const option of base) {
      if (!seen.has(option.field)) {
        seen.add(option.field);
        suggestionOptions.push(option);
      }
    }
    for (const option of scopedSuggestions) {
      if (!seen.has(option.field)) {
        seen.add(option.field);
        suggestionOptions.push(option);
      }
    }
    const breadthOnly: FieldOptions[] = [];
    for (const option of breadthAttrs) {
      if (!seen.has(option.field)) {
        seen.add(option.field);
        breadthOnly.push(option);
      }
    }

    const committed = new Set(committedContextAttributeFields(parsedQuery));

    let offerable: FieldOptions[];
    if (selectedKinds.length > 0) {
      const breadthSlots =
        breadthOnly.length > 0
          ? Math.min(
              breadthOnly.length,
              suggestionOptions.length >= PINNED_KIND_ATTRIBUTE_LIMIT
                ? PINNED_KIND_BREADTH_RESERVED
                : PINNED_KIND_ATTRIBUTE_LIMIT - suggestionOptions.length,
            )
          : 0;
      const suggestionSlots = Math.min(suggestionOptions.length, PINNED_KIND_ATTRIBUTE_LIMIT - breadthSlots);
      offerable = [...suggestionOptions.slice(0, suggestionSlots), ...breadthOnly.slice(0, breadthSlots)];
    } else {
      offerable = [...suggestionOptions, ...breadthOnly];
    }

    // Keep FieldOptions for committed filters beyond the cap so lozenges keep trimmed displayValue.
    const offerableFields = new Set(offerable.map((option) => option.field));
    const committedExtras: FieldOptions[] = [];
    for (const option of [...suggestionOptions, ...breadthOnly, ...fieldOptions]) {
      if (committed.has(option.field) && !offerableFields.has(option.field)) {
        offerableFields.add(option.field);
        committedExtras.push(option);
      }
    }
    return committedExtras.length > 0 ? [...offerable, ...committedExtras] : offerable;
  }, [fieldOptions, selectedKinds, serverScopedOptions, breadthOptions, parsedQuery]);

  const showEmptyPinnedAttributesMessage = useMemo(() => {
    if (selectedKinds.length === 0 || isAttributeCatalogPending) {
      return false;
    }
    return !scopedFieldOptions.some(
      (option) => option.field !== CONTEXT_KIND_FIELD && option.field.startsWith(CONTEXT_ATTRS_PREFIX),
    );
  }, [selectedKinds.length, isAttributeCatalogPending, scopedFieldOptions]);

  const onLoadValues = useCallback<LoadFieldValues>(
    async (field) => {
      const params = valueParamsByFieldRef.current.get(field);
      if (!params || !environmentKey) {
        return [];
      }
      try {
        const data = await queryClient.fetchQuery(
          getContextAttributeValuesQuery({
            projectKey,
            environmentKey,
            attributeName: params.attributeName,
            query: { filter: `kind equals ${JSON.stringify(params.kind)}`, limit: PINNED_KIND_ATTRIBUTE_VALUE_LIMIT },
          }),
        );
        const seen = new Set<string>();
        const values: Array<{ value: string; approxCount?: number }> = [];
        for (const item of data?.items?.[0]?.values ?? []) {
          const value = String(item.name);
          if (!value || seen.has(value)) {
            continue;
          }
          seen.add(value);
          values.push({ value, approxCount: item.weight });
        }
        return ensureBooleanSampleValues(values, (value) => ({ value }));
      } catch {
        return [];
      }
    },
    [environmentKey, projectKey, queryClient, valueParamsByFieldRef],
  );

  return { scopedFieldOptions, onLoadValues, isAttributeCatalogPending, showEmptyPinnedAttributesMessage };
}
